import { cleanPermalink } from './top-videos';
import { format } from 'date-fns';
import type { DateRange } from 'react-day-picker';

import type { MediaStats } from '../types/statistics';
import { fetchCropBounds } from './crop-bounds-api';
import { getAccessToken, staleGoogleTokenError } from './google-auth';

const DOCS_API = 'https://docs.googleapis.com/v1/documents';
export const DOCS_SCOPE = 'https://www.googleapis.com/auth/documents';

export async function createDoc(
  token: string,
  title = 'Отчет по выполненным роликам',
): Promise<string> {
  const res = await fetch(DOCS_API, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ title }),
  });
  if (!res.ok) throw staleGoogleTokenError(res.status) ?? new Error(`Ошибка создания документа: ${res.status}`);
  const data = await res.json();
  return data.documentId as string;
}

export async function applyBatchUpdate(
  token: string,
  docId: string,
  requests: object[],
): Promise<{ replies: { insertInlineImage?: { objectId: string } }[] }> {
  const res = await fetch(`${DOCS_API}/${docId}:batchUpdate`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ requests }),
  });
  if (!res.ok) throw staleGoogleTokenError(res.status) ?? new Error(`Ошибка форматирования документа: ${res.status}`);
  return res.json();
}


export type LineSpec = {
  text: string;
  center?: boolean;
  bold?: boolean;
  linkUrl?: string;
  /** Смещение начала ссылки внутри строки: 0 (по умолчанию) — ссылка на всю строку. */
  linkFrom?: number;
  /** Заливка строки: белый текст на чёрном фоне. */
  highlight?: boolean;
  /**
   * Висячий отступ в PT: первая строка начинается от поля, а таб внутри строки
   * прыгает ровно на эту позицию. Так вторая колонка (ссылки) встаёт ровно,
   * независимо от длины имени аккаунта.
   */
  tabIndentPt?: number;
  hangingIndent?: boolean; // block indent: all lines start at 72PT (indentStart 72PT, indentFirstLine 72PT)
};

/**
 * Строки → текст документа + запросы форматирования Docs API.
 * `lineStarts[i]` — индекс начала i-й строки в документе (нужен, чтобы знать,
 * куда потом вставлять картинки).
 */
export function renderLines(
  lines: LineSpec[],
  offset: number,
): { text: string; formatRequests: object[]; lineStarts: number[] } {
  const text = lines.map((l) => l.text).join('\n');
  const formatRequests: object[] = [];
  const lineStarts: number[] = [];
  let idx = offset;

  for (const line of lines) {
    lineStarts.push(idx);
    const len = line.text.length + 1;

    if (line.center) {
      formatRequests.push({
        updateParagraphStyle: {
          range: { startIndex: idx, endIndex: idx + len },
          paragraphStyle: { alignment: 'CENTER' },
          fields: 'alignment',
        },
      });
    }

    if (line.bold) {
      formatRequests.push({
        updateTextStyle: {
          range: { startIndex: idx, endIndex: idx + len - 1 },
          textStyle: { bold: true },
          fields: 'bold',
        },
      });
    }

    if (line.highlight) {
      formatRequests.push({
        updateTextStyle: {
          range: { startIndex: idx, endIndex: idx + len - 1 },
          textStyle: {
            backgroundColor: { color: { rgbColor: { red: 0, green: 0, blue: 0 } } },
            foregroundColor: { color: { rgbColor: { red: 1, green: 1, blue: 1 } } },
          },
          fields: 'backgroundColor,foregroundColor',
        },
      });
    }

    if (line.linkUrl) {
      formatRequests.push({
        updateTextStyle: {
          range: { startIndex: idx + (line.linkFrom ?? 0), endIndex: idx + len - 1 },
          textStyle: { link: { url: line.linkUrl } },
          fields: 'link',
        },
      });
    }

    if (line.hangingIndent) {
      formatRequests.push({
        updateParagraphStyle: {
          range: { startIndex: idx, endIndex: idx + len },
          paragraphStyle: {
            indentStart: { magnitude: 72, unit: 'PT' },
            indentFirstLine: { magnitude: 72, unit: 'PT' },
            alignment: 'START',
          },
          fields: 'indentStart,indentFirstLine,alignment',
        },
      });
    }

    if (line.tabIndentPt !== undefined) {
      formatRequests.push({
        updateParagraphStyle: {
          range: { startIndex: idx, endIndex: idx + len },
          paragraphStyle: {
            indentStart: { magnitude: line.tabIndentPt, unit: 'PT' },
            indentFirstLine: { magnitude: 0, unit: 'PT' },
            alignment: 'START',
          },
          fields: 'indentStart,indentFirstLine,alignment',
        },
      });
    }

    idx += len;
  }

  return { text, formatRequests, lineStarts };
}

// 1 inch = 72 PT; 1 cm = 72/2.54 PT
const IMAGE_WIDTH_PT = 88.44; // 3.12 cm

export type ReportPage = {
  posts: MediaStats[];
  imagePost1: MediaStats | undefined;
  imagePost2: MediaStats | undefined;
};

type PageContent = {
  text: string;
  formatRequests: object[];
  image1Index: number | null;
};

export function needsTranslation(text: string): boolean {
  // Kazakh-specific Cyrillic letters absent from Russian
  return /[ӘәҒғҚқҢңӨөҰұҮүҺһІі]/.test(text);
}

function getImageUrl(post: MediaStats | undefined): string | null {
  if (!post) return null;
  return post.contentType === 'REELS' || post.contentType === 'VIDEO'
    ? post.thumbnailUrl
    : post.mediaUrl;
}

function buildPageContent(
  page: ReportPage,
  dateRange: DateRange | undefined,
  offset: number,
  pageNumber: number,
  isFirstPage: boolean,
  translatedCaption?: string,
  textGoal?: string,
): PageContent {
  const lines: LineSpec[] = [];

  if (isFirstPage) {
    lines.push({ text: 'Отчет по выполненным роликам.', center: true, bold: true });
    lines.push({ text: '' });
    if (dateRange?.from) {
      const from = format(dateRange.from, 'dd.MM.yyyy');
      const to = dateRange.to ? format(dateRange.to, 'dd.MM.yyyy') : from;
      lines.push({ text: `${from} - ${to}`, bold: true });
    }
    lines.push({ text: '' });
  }

  const first = page.posts[0];
  const captionText = translatedCaption ?? (() => {
    if (!first?.caption) return null;
    return first.caption.split(/\n\n+/).find(p => p.trim())?.trim() ?? null;
  })();
  if (captionText) {
    const heading = captionText.match(/^[^.!?]*[.!?]/)?.[0]?.trim() ?? captionText;
    lines.push({ text: `${pageNumber}. ${heading}`, bold: true, hangingIndent: true });
    lines.push({ text: '' });
  }
  if (first?.publishedAt) {
    lines.push({ text: `\t${format(new Date(first.publishedAt), 'dd.MM.yyyy')}` });
    lines.push({ text: '' });
  }
  if (textGoal) {
    lines.push({ text: textGoal });
  }

  lines.push({ text: '' });

  for (const r of page.posts) {
    lines.push({ text: r.accountUsername });
    lines.push({ text: cleanPermalink(r.url), linkUrl: cleanPermalink(r.url) });
  }

  lines.push({ text: '' });

  const imagePlaceholderLineIdx = lines.length;
  lines.push({ text: '' });

  const total = page.posts.reduce((sum, r) => sum + r.impressionsTotal, 0);
  lines.push({ text: `Показы - ${total.toLocaleString('ru-RU')}` });

  const { text, formatRequests, lineStarts } = renderLines(lines, offset);
  const image1Index = lineStarts[imagePlaceholderLineIdx] ?? null;

  return { text, formatRequests, image1Index };
}

/**
 * Собирает отчёт в Google Docs. Цель текста пишет OpenAI, и без неё документ
 * всё равно строится — поэтому сбой не бросается, а возвращается в `goalError`
 * (текст первой ошибки), чтобы кнопка показала его, а не молча пропустила строку.
 */
export async function generateGoogleDocsReport(
  pages: ReportPage[],
  dateRange: DateRange | undefined,
): Promise<{ goalError: string | null }> {
  const nonEmpty = pages.filter(p => p.posts.length > 0);
  if (nonEmpty.length === 0) throw new Error('Нет данных для отчёта');

  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (!clientId) throw new Error('VITE_GOOGLE_CLIENT_ID не задан в .env');

  const token = await getAccessToken(DOCS_SCOPE);
  const docId = await createDoc(token);

  let goalError: string | null = null;

  // Translate captions and generate text goals concurrently for all pages
  const { openaiApi } = await import('../../../shared/api/openai.api');
  const [translatedCaptions, textGoals] = await Promise.all([
    Promise.all(
      nonEmpty.map(async (page) => {
        const caption = page.posts[0]?.caption;
        if (!caption) return undefined;
        const firstPara = caption.split(/\n\n+/).find(p => p.trim())?.trim();
        if (!firstPara) return undefined;
        if (!needsTranslation(firstPara)) return firstPara;
        try {
          const result = await openaiApi.translateText(firstPara);
          return result.translation;
        } catch {
          return firstPara;
        }
      }),
    ),
    Promise.all(
      nonEmpty.map(async (page, i) => {
        const caption = page.posts[0]?.caption;
        if (!caption) {
          console.warn(`[export-docs] Page ${i + 1}: caption is null/empty, skipping textGoal generation`);
          return undefined;
        }
        try {
          const result = await openaiApi.generateTextGoal(caption);
          console.log(`[export-docs] Page ${i + 1}: textGoal generated:`, result.goal);
          return result.goal;
        } catch (err) {
          console.error(`[export-docs] Page ${i + 1}: generateTextGoal failed:`, err);
          goalError ??= err instanceof Error ? err.message : 'Ошибка генерации цели';
          return undefined;
        }
      }),
    ),
  ]);

  // Build each page content; pages joined by \n (1 char), page breaks inserted separately.
  const pageContents: PageContent[] = [];
  const pageBreakIndices: number[] = [];
  let currentOffset = 1;

  for (let i = 0; i < nonEmpty.length; i++) {
    const pc = buildPageContent(nonEmpty[i], dateRange, currentOffset, i + 1, i === 0, translatedCaptions[i], textGoals[i]);
    pageContents.push(pc);
    currentOffset += pc.text.length;
    if (i < nonEmpty.length - 1) {
      currentOffset += 1; // +1 for \n separator
      pageBreakIndices.push(currentOffset); // break goes at start of next page
    }
  }

  const fullText = pageContents.map(pc => pc.text).join('\n');
  const allFormatRequests = pageContents.flatMap(pc => pc.formatRequests);
  const totalTextLen = fullText.length;

  await applyBatchUpdate(token, docId, [
    { insertText: { location: { index: 1 }, text: fullText } },
    // Apply Times New Roman to all inserted text
    {
      updateTextStyle: {
        range: { startIndex: 1, endIndex: totalTextLen },
        textStyle: { weightedFontFamily: { fontFamily: 'Times New Roman', weight: 400 } },
        fields: 'weightedFontFamily',
      },
    },
    ...allFormatRequests,
  ]);

  // Insert page breaks last-to-first so earlier indices stay valid
  for (let i = pageBreakIndices.length - 1; i >= 0; i--) {
    try {
      await applyBatchUpdate(token, docId, [{
        insertPageBreak: { location: { index: pageBreakIndices[i] } },
      }]);
    } catch { /* non-fatal */ }
  }

  // Crop images client-side via proxy + upload to Supabase Storage
  const allImageUrls = nonEmpty.flatMap(page => [
    getImageUrl(page.imagePost1),
    getImageUrl(page.imagePost2),
  ]).filter((u): u is string => u !== null);
  const croppedUrlMap = await fetchCropBounds(allImageUrls);

  // Insert images last-to-first. Each page i has i page breaks before it → shift index by i.
  for (let i = pageContents.length - 1; i >= 0; i--) {
    const pc = pageContents[i];
    const page = nonEmpty[i];
    if (pc.image1Index === null) continue;

      // insertPageBreak inserts 2 chars (page break + newline); page i has i breaks before it
    const adjustedIndex = pc.image1Index + i * 2;
    const url1 = getImageUrl(page.imagePost1);
    const url2 = getImageUrl(page.imagePost2);
    const insertUrl1 = url1 ? (croppedUrlMap.get(url1) ?? url1) : null;
    const insertUrl2 = url2 ? (croppedUrlMap.get(url2) ?? url2) : null;

    try {
      if (insertUrl1) {
        await applyBatchUpdate(token, docId, [{
          insertInlineImage: {
            location: { index: adjustedIndex },
            uri: insertUrl1,
            objectSize: { width: { magnitude: IMAGE_WIDTH_PT, unit: 'PT' } },
          },
        }]);
      }

      if (insertUrl2) {
        await applyBatchUpdate(token, docId, [{
          insertInlineImage: {
            location: { index: adjustedIndex + 1 },
            uri: insertUrl2,
            objectSize: { width: { magnitude: IMAGE_WIDTH_PT, unit: 'PT' } },
          },
        }]);
      }
    } catch {
      // Image insertion is non-fatal
    }
  }

  window.open(`https://docs.google.com/document/d/${docId}/edit`, '_blank');
  return { goalError };
}
